import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import TeamGrid from '../components/TeamGrid';

gsap.registerPlugin(ScrollTrigger);

export default function AboutSection({ onNavigate }) {
  const sectionRef = useRef(null);
  const img1Ref = useRef(null);
  const img2Ref = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // index.css:114-120 neutraliza las transiciones CSS con
    // prefers-reduced-motion, pero GSAP escribe estilos inline por JS y se
    // salta esa regla. Como todos los tweens de aquí son from/fromTo, no
    // ejecutarlos deja los elementos en su estado natural: visibles y en su
    // sitio. Va antes del gsap.context para que el cleanup no llame a
    // ctx.revert() sobre un ctx inexistente.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      // Statement animations
      gsap.from('.about-label', { y: 30, opacity: 0, duration: 0.8, ease: 'expo.out', scrollTrigger: { trigger: '.about-statement', start: 'top 80%' } });
      gsap.from('.about-heading', { y: 40, opacity: 0, duration: 0.8, ease: 'expo.out', delay: 0.1, scrollTrigger: { trigger: '.about-statement', start: 'top 80%' } });
      gsap.from('.about-body', { y: 30, opacity: 0, duration: 0.8, ease: 'expo.out', delay: 0.2, scrollTrigger: { trigger: '.about-statement', start: 'top 80%' } });
      gsap.from('.about-cta', { y: 20, opacity: 0, duration: 0.8, ease: 'expo.out', delay: 0.3, scrollTrigger: { trigger: '.about-statement', start: 'top 80%' } });

      // Flanking images parallax
      if (img1Ref.current) {
        gsap.to(img1Ref.current, { y: -60, ease: 'none', scrollTrigger: { trigger: '.about-statement', start: 'top bottom', end: 'bottom top', scrub: 0.5 } });
      }
      if (img2Ref.current) {
        gsap.to(img2Ref.current, { y: 60, ease: 'none', scrollTrigger: { trigger: '.about-statement', start: 'top bottom', end: 'bottom top', scrub: 0.5 } });
      }

      // Team animations
      // fromTo y NO from: index.css:67-70 pone `img { transition: opacity .3s }`
      // a todas las imágenes, y un .from() escribe opacity:0 inline, dispara la
      // transición CSS y GSAP acaba tomando un valor a medio camino como
      // destino: el elemento se queda invisible. Ver HeroSection.jsx:55-68.
      gsap.fromTo('.team-label', { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'expo.out', scrollTrigger: { trigger: '.team-block', start: 'top 80%' } });
      gsap.fromTo('.team-heading', { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'expo.out', delay: 0.1, scrollTrigger: { trigger: '.team-block', start: 'top 80%' } });
      gsap.fromTo('.team-line', { scaleX: 0 }, { scaleX: 1, duration: 1, ease: 'expo.out', delay: 0.2, scrollTrigger: { trigger: '.team-block', start: 'top 80%' } });

      // Un trigger POR grupo, no uno para los 7: los tres tiers ocupan más de
      // una pantalla y un stagger único dispararía la entrada de "Equipo"
      // mientras el visitante todavía mira "Socios".
      section.querySelectorAll('.team-group').forEach((grupo) => {
        gsap.fromTo(grupo.querySelectorAll('.team-item'),
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: 'expo.out', scrollTrigger: { trigger: grupo, start: 'top 85%' } });
      });

      // Manifesto animations
      gsap.from('.manifesto-kicker', { opacity: 0, y: 20, duration: 0.8, ease: 'expo.out', scrollTrigger: { trigger: '.manifesto', start: 'top 80%' } });
      gsap.from('.manifesto-word', {
        y: 80, opacity: 0, duration: 1, stagger: 0.12, ease: 'expo.out',
        scrollTrigger: { trigger: '.manifesto', start: 'top 75%' }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="nosotros">
      {/* Part A: Firm Statement */}
      <div className="about-statement relative w-full bg-[#e8e2d6] py-32 md:py-44 overflow-hidden">
        {/* Flanking images */}
        <img
          ref={img1Ref}
          src="/assets/editorial-1.jpg"
          alt=""
          className="absolute left-0 top-[15%] w-[22vw] max-w-[280px] opacity-30 pointer-events-none hidden lg:block"
          style={{ transform: 'translateX(-35%)', filter: 'grayscale(30%)' }}
          loading="lazy"
        />
        <img
          ref={img2Ref}
          src="/assets/editorial-2.jpg"
          alt=""
          className="absolute right-0 top-[25%] w-[22vw] max-w-[280px] opacity-30 pointer-events-none hidden lg:block"
          style={{ transform: 'translateX(35%)', filter: 'grayscale(30%)' }}
          loading="lazy"
        />

        <div className="max-w-[800px] mx-auto px-6 text-center relative z-10">
          <span className="about-label block font-mono text-[11px] uppercase tracking-[0.2em] text-[#C9A26E] mb-8">
            La Firma
          </span>
          <h2 className="about-heading font-heading text-[#1a1a1a] text-[clamp(32px,5vw,64px)] leading-[1.05] tracking-[-0.02em] mb-8">
            No somos asesores externos.
          </h2>
          <div className="w-10 h-px bg-[#C9A26E] mx-auto mb-8" />
          <p className="about-body font-body text-[#5a544c] text-lg md:text-xl leading-[1.8] max-w-[560px] mx-auto mb-10">
            Somos el orden que la familia necesita para seguir unida a través del tiempo y los cambios.
          </p>
          <a
            href="#contacto"
            onClick={(e) => { e.preventDefault(); onNavigate('contacto'); }}
            className="about-cta inline-flex items-center gap-2 font-body text-[12px] font-medium uppercase tracking-[0.1em] text-[#C9A26E] hover:text-[#1a1a1a] transition-colors duration-400 group"
          >
            Conoce la Firma
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="group-hover:translate-x-1 transition-transform duration-300">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Part B: Manifesto */}
      <div className="manifesto relative w-full bg-[#14181f] py-32 md:py-44 overflow-hidden">
        {/* Subtle texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <div className="max-w-[1000px] mx-auto px-6 relative z-10">
          <p className="manifesto-kicker font-heading italic text-[#C9A26E] text-lg md:text-xl mb-12 md:mb-16">
            El orden que la familia necesita
          </p>

          <div className="space-y-1 md:space-y-2" aria-hidden="true">
            {['We', 'are', 'the', 'governance', 'they', 'need'].map((word, i) => (
              <div key={i} className="overflow-hidden">
                <div
                  className={`manifesto-word font-heading text-[clamp(48px,10vw,120px)] leading-[0.9] tracking-[-0.03em] ${
                    i < 3 ? 'text-[#2a3038]' : 'text-[#f0ebe2]'
                  }`}
                >
                  {word}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Part C: Equipo */}
      <TeamGrid />
    </section>
  );
}
